import {
  Avatar,
  Button,
  Caption,
  CheckIcon,
  Chip,
  ChipClickable,
  Heading,
  Link,
  LocationIcon,
  Popover,
  Separator,
  UserAddIcon,
  VerifiedIcon,
} from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import classNames from 'classnames'
import { AnchorHTMLAttributes, Fragment } from 'react'

import { useProfileFollow } from '../../../hooks'
import { profileTypeLabelMapping } from '../constants'
import styles from '../profile-quickview.module.scss'

export type Profile = {
  __typename: 'Profile'
  id: string
  fullname: string | null
  url: string | null
  followingAmount: number | null
  followersAmount: number | null
  isLawyer: boolean | null
  profileType: string | null
  isVerified: boolean | null
  isInstitutional: boolean | null
  hasProBadge: boolean | null
  entityId: any | null
  entityType: string | null
  isFollowedByViewer: boolean | null
  cover: { url: string | null } | null
  avatar: { url2xl: string | null } | null
  occupation: { title: string | null } | null
  homeLocation: { title: string | null } | null
} | null

export interface ProfileQuickviewContentProps {
  profile: Profile
}

export function ProfileQuickviewContent({ profile }: ProfileQuickviewContentProps) {
  const follow = useProfileFollow({ profile })

  const {
    profileType,
    isInstitutional,
    isLawyer,
    isVerified,
    occupation,
    url,
    cover,
    avatar,
    fullname,
    hasProBadge,
    homeLocation,
    followersAmount,
    followingAmount,
  } = profile || {}

  const occupationTitle = occupation?.title || ''
  const labelText =
    (isInstitutional && profileType && profileTypeLabelMapping[profileType]) || occupationTitle

  const allLinkProps = {
    target: '_blank',
    rel: 'noopener',
  } satisfies AnchorHTMLAttributes<HTMLAnchorElement>

  const chips: React.ReactNode[] = []
  if (isLawyer) {
    chips.push(
      <ChipClickable size="md" asChild>
        <a href="https://www.jusbrasil.com.br/advogados/home" {...allLinkProps}>
          Advogado online
        </a>
      </ChipClickable>
    )
  } else if (labelText) {
    chips.push(<Chip size="md">{labelText}</Chip>)
  }
  if (hasProBadge) {
    chips.push(
      <ChipClickable size="md" asChild>
        <a
          href="https://www.jusbrasil.com.br/pro?from-component=ProfileOccupation"
          {...allLinkProps}
        >
          Assinante
        </a>
      </ChipClickable>
    )
  }

  return (
    <>
      <a href={url!} className={styles.cover} {...allLinkProps}>
        {cover?.url && (
          <Image src={cover.url} alt={`Capa de ${fullname}`} width={320} height={112} />
        )}
      </a>

      <div className={styles.content}>
        <div className={styles.avatarArea}>
          <a href={url!} {...allLinkProps}>
            <Avatar
              src={avatar?.url2xl || undefined}
              alt={[fullname, occupationTitle].filter(Boolean).join(', ')}
              size="2xl"
              className={styles.avatar}
              loading="lazy"
              itemProp="image"
            />
          </a>
        </div>

        {chips.length > 0 && (
          <div className={styles.chips}>
            {chips.map((chip, idx) => (
              <Fragment key={idx}>{chip}</Fragment>
            ))}
          </div>
        )}

        <Heading size="sm" className={styles.title} asChild>
          <h3>
            <Link type="ghost" href={url!} {...allLinkProps}>
              {fullname}
            </Link>

            {isVerified && (
              <Popover>
                <Popover.Trigger asChild>
                  <button tabIndex={-1}>
                    <VerifiedIcon size="sm" className={styles.verifiedIcon} />
                  </button>
                </Popover.Trigger>
                <Popover.Content>
                  <Popover.ContentTitle>
                    <div className={styles.verifiedPopoverTitle}>
                      <VerifiedIcon className={styles.verifiedIcon} />
                      Perfil verificado
                    </div>
                  </Popover.ContentTitle>
                  <Popover.ContentDescription className={styles.verifiedPopoverDescription}>
                    O Jusbrasil confirmou que este perfil é autêntico.
                  </Popover.ContentDescription>
                </Popover.Content>
              </Popover>
            )}
          </h3>
        </Heading>

        {homeLocation && (
          <Caption className={styles.location}>
            <LocationIcon size="xs" />
            {homeLocation.title}
          </Caption>
        )}
      </div>

      <Separator className={styles.separator} />

      <div className={styles.actions}>
        <div className={styles.actionsCenter}>
          <Heading size="sm">{followersAmount || 0}</Heading>
          <Caption>
            <Link type="ghost" href={`${url}/seguidores`} {...allLinkProps}>
              Seguidores
            </Link>
          </Caption>
        </div>

        <div className={classNames(styles.actionsCenter, styles.actionsExpand)}>
          <Heading size="sm">{followingAmount || 0}</Heading>
          <Caption>
            <Link type="ghost" href={`${url}/seguidos`} {...allLinkProps}>
              Seguindo
            </Link>
          </Caption>
        </div>

        <div>
          <Button
            leftIcon={follow.isFollowing ? <CheckIcon /> : <UserAddIcon />}
            loading={follow.loading}
            onClick={follow.toggle}
          >
            {follow.isFollowing ? 'Seguindo' : 'Seguir'}
          </Button>
        </div>
      </div>
    </>
  )
}
